import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    Checkbox,
    RadioGroup,
    Radio,
    Stack,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PhoneDialCode from './partials/phone-dial-code'
import Birthdate from './partials/birth-date'
import {VisibilityIcon, VisibilityOffIcon} from '@salesforce/retail-react-app/app/components/icons'
import {useIntl} from 'react-intl'

const Field = ({
    googleMapsRef,
    name,
    label,
    formLabel,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    control,
    autoComplete,
    defaultValue,
    helpText,
    children,
    inputRef,
    showLabel = true,
    errorLeft,
    controlProps = {},
    setCustomError = () => {},
    ...rest
}) => {
    const intl = useIntl()
    const [hidePassword, setHidePassword] = useState(true)
    const PasswordIcon = hidePassword ? VisibilityIcon : VisibilityOffIcon
    const passwordIconLabel = hidePassword
        ? intl.formatMessage({
              id: 'field.password.assistive_msg.show_password',
              defaultMessage: 'Show password'
          })
        : intl.formatMessage({
              id: 'field.password.assistive_msg.hide_password',
              defaultMessage: 'Hide password'
          })
    const inputType =
        type === 'password' && hidePassword ? 'password' : type === 'password' ? 'text' : type
    const styles = useMultiStyleConfig('formFields')

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, value, ref}}) => {
                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps

                return (
                    <FormControl id={name} isInvalid={error} {...controlProps}>
                        {!['checkbox', 'hidden'].includes(type) &&
                            showLabel !== false &&
                            (formLabel || <FormLabel {...styles.formlabel}>{label}</FormLabel>)}

                        <InputGroup>
                            {['text', 'password', 'email', 'tel', 'number'].includes(type) && (
                                <Input
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                        if (googleMapsRef) {
                                            googleMapsRef.current = node
                                        }
                                    }}
                                    variant="address"
                                    onChange={onChange}
                                    value={value}
                                    type={inputType}
                                    placeholder={placeholder}
                                    autoComplete={autoComplete}
                                    {..._inputProps}
                                />
                            )}

                            {type === 'fiscalcode' && (
                                <Input
                                    variant={'address'}
                                    value={value || ''}
                                    onChange={(e) => {
                                        const inputValue = e.target.value.toUpperCase()
                                        onChange(inputValue)
                                    }}
                                    maxLength={16}
                                    textTransform="uppercase"
                                    {...rest}
                                />
                            )}

                            {type === 'dialcode' && (
                                <PhoneDialCode
                                    onChange={onChange}
                                    value={value}
                                    placeholder={placeholder}
                                    {...rest}
                                />
                            )}

                            {type === 'birthday' && (
                                <Birthdate
                                    value={value}
                                    onChange={(dobString) => onChange(dobString)}
                                    placeholder={placeholder}
                                    setCustomError={setCustomError}
                                    {...rest}
                                />
                            )}

                            {type === 'hidden' && (
                                <input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}

                            {type === 'password' && (
                                <InputRightElement>
                                    <IconButton
                                        variant="ghosted"
                                        aria-label={passwordIconLabel}
                                        icon={<PasswordIcon color="gray.500" boxSize={6} />}
                                        onClick={() => setHidePassword(!hidePassword)}
                                    />
                                </InputRightElement>
                            )}

                            {type === 'select' && (
                                <Select
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    placeholder={placeholder}
                                    {...rest}
                                >
                                    {options.map((opt) => (
                                        <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {type === 'checkbox' && (
                                <Checkbox
                                    ref={ref}
                                    onChange={(e) => onChange(e.target.checked)}
                                    isChecked={value}
                                    {..._inputProps}
                                >
                                    {formLabel || label}
                                </Checkbox>
                            )}
                            {type === 'radio' && (
                                <RadioGroup
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    {..._inputProps}
                                >
                                    <Stack
                                        direction="row"
                                        mt={{base: '0.4rem', md: '0rem'}}
                                        mb={{base: '0.4rem', md: '0rem'}}
                                        gap={6}
                                    >
                                        {options.map((opt) => (
                                            <Radio
                                                key={`${opt.label}-${opt.value}`}
                                                value={opt.value}
                                            >
                                                {opt.label}
                                            </Radio>
                                        ))}
                                    </Stack>
                                </RadioGroup>
                            )}
                            {children}
                        </InputGroup>

                        {error && type !== 'hidden' && (
                            <FormErrorMessage
                                position={'absolute'}
                                top={'100%'}
                                left={errorLeft || {base: '0%', md: '33%', lg: '34%'}}
                            >
                                {error.message}
                            </FormErrorMessage>
                        )}

                        {helpText}
                    </FormControl>
                )
            }}
        />
    )
}

Field.propTypes = {
    googleMapsRef: PropTypes.object,
    name: PropTypes.string,
    label: PropTypes.string,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    showLabel: PropTypes.bool,
    type: PropTypes.oneOf([
        'text',
        'dialcode',
        'fiscalcode',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'radio',
        'hidden',
        'birthday'
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    children: PropTypes.any,
    errorLeft: PropTypes.string,
    inputRef: PropTypes.object,
    controlProps: PropTypes.object,
    setCustomError: PropTypes.func
}

export default Field
